/* eslint-disable no-console */
import { useState, useEffect } from 'react';
import useTranslation from 'Hooks/useTranslation';
import { useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import { S3_IMAGES, TERMINAL_BASE } from 'Constants/General';
import { colors } from 'Constants/Colors';
import { TextField, FormHelperText, Typography, Select, MenuItem } from '@mui/material';
import Modal from 'Components/Modal';
import Alert from 'Components/Alert';
import SelectInput from 'Components/FormComponents/SelectInput';
import FormValidations from 'Utils/FormValidations';
import { s3 } from 'Services/AWS/s3';
import SaveInfoModal from './Components/SaveInfoModal';
import useFetch from './Hooks/useFetch';
import useModal from './Hooks/useModal';
import {
  hr,
  mainContainer,
  fieldsContainer,
  buttonStyle,
  buttonsContainer,
  InputContainer,
  ColumnContainer,
  textInputStyle,
  ImageUploadContainer,
  redAsterix,
} from './style';

import ImageUploadComponent from './Components/ImageUpload';

export default function EditCourseInformation({ handleNameChange }) {
  const { t2 } = useTranslation('EditCourse');
  const location = useLocation();
  const courseId = location?.state?.course[(location?.state?.course?.length || 0) - 1];
  const [open, setOpen] = useState(false);
  const [unchanged, setUnchanged] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState();
  const [formData, setFormData] = useState({});
  const { openModal, setOpenModal } = useModal();

  const { course, categories, priorities, setCourseBasicInformation } = useFetch(courseId);
  const [minutes, setMinutes] = useState('');

  const expirationTime = [
    { id: 0, name: t2('Never') },
    { id: 3, name: `3 ${t2('Months')}` },
    { id: 6, name: `6 ${t2('Months')}` },
    { id: 12, name: `12 ${t2('Months')}` },
    { id: 18, name: `18 ${t2('Months')}` },
  ];

  useEffect(() => {
    if (!formData.name && course.name) {
      const [hours = '00'] = course.trainingHours?.split(':') || [];
      setFormData({
        ...course,
        trainingHours: hours,
      });
      setMinutes(course.trainingHours?.split(':')[1] || '00');
      setIsLoading(false);
    }
  }, [course, formData]);

  const handleChange = event => {
    setUnchanged(false);
    if (event.target.name === 'courseName') {
      setFormData({ ...formData, name: event.target.value });
      return null;
    }
    if (event.target.name === 'trainingHoursMinutes') {
      setMinutes(event.target.value);
      return null;
    }
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    return null;
  };

  const handleImageUpload = file => {
    setUnchanged(false);
    const extension = file.name.split('.').slice(-1)[0];
    const fileData = {
      file,
      extension,
    };
    setFormData({
      ...formData,
      thumbnail: fileData,
    });
  };

  const handleSubmit = async () => {
    let key = formData.thumbnail;
    const { file, extension } = formData.thumbnail;

    const isFileUploaded = file !== undefined && extension !== undefined;

    if (isFileUploaded === true) {
      try {
        key = `${S3_IMAGES}/${formData.id}/CourseImage.${extension}`;
        await s3.uploadFileToBucketWithKeyProvided(key, file, 'image/jpeg image/png image/jpg');
      } catch (caught) {
        console.error(`Error ${caught}`);
        return false;
      }
    }

    const hours = formData.trainingHours?.padStart(2, '0') || '00';
    const newMinutes = minutes.padStart(2, '0') || '00';
    const trainingTime = `${hours}:${newMinutes}:00`;

    const response = await setCourseBasicInformation({
      ...formData,
      thumbnail: key,
      trainingHours: trainingTime,
      terminals: [TERMINAL_BASE],
    });

    if (response?.id) {
      handleNameChange(formData.name);
      setOpen(true);
    }

    setOpenModal(false);
    return true;
  };

  const validate = () => {
    const temp = {};

    temp.courseName = formData.name.length >= 3 ? '' : t2('FormCourseNameError');
    temp.category = formData.category ? '' : t2('FormCourseCategoryError');
    temp.priority = formData.priority ? '' : t2('FormCoursePriorityError');
    // temp.terminals =
    //   formData.terminals.length !== 0 ? '' : t2('FormCourseTerminalError');
    temp.expirationTime = formData.expirationTime !== '' ? '' : t2('FormCourseExpirationTimeError');

    temp.description = FormValidations.courseDescription(formData?.description);

    setErrors({
      ...temp,
    });

    return Object.values(temp).every(x => x === '');
  };

  const submitHandler = e => {
    e.preventDefault();
    const isValid = validate();

    if (isValid) {
      setOpenModal(true);
    }
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  if (isLoading) {
    return <div>{t2('Loading')}</div>;
  }

  const asterix = <div style={redAsterix}>*</div>;
  const today = new Date();
  today.setMonth(today.getMonth() + 5);

  return (
    <div className="col" style={mainContainer}>
      <h3>{t2('BasicInformation')}</h3>
      <hr style={hr} />
      <form className="col" onSubmit={e => submitHandler(e)}>
        <div style={fieldsContainer}>
          <ColumnContainer>
            <InputContainer htmlFor="courseName">
              <div>
                {t2('CourseName')} {asterix}
              </div>
              <TextField
                name="courseName"
                placeholder={t2('CourseName')}
                value={formData?.name || ''}
                onChange={handleChange}
                type="text"
                color="success"
                sx={{
                  input: { ...textInputStyle },
                }}
                {...(errors?.courseName && {
                  error: true,
                  label: 'Error',
                  helperText: errors.courseName,
                })}
              />
            </InputContainer>
            <InputContainer htmlFor="category">
              <div>
                {t2('Category')} {asterix}
              </div>
              {categories.length > 0 && (
                <SelectInput
                  name="category"
                  placeholder={t2('Category')}
                  value={formData?.category || ''}
                  onChange={handleChange}
                  sx={{ backgroundColor: colors.WHITE }}
                  disableunderline="true"
                  options={categories}
                  errortext={errors?.category}
                />
              )}
            </InputContainer>
            {/* <InputContainer htmlFor="terminal">
              <div>
                {t2('Terminal')} {asterix}
              </div>
              {terminals.length > 0 && formData?.terminals && (
                <MultiSelectInput
                  name="terminal"
                  data={terminals}
                  selected={formData?.terminals || []}
                  onChange={handleTerminalsSelect}
                  placeHolderText={t2('Terminal')}
                />
              )}
              {errors?.terminals && (
                <FormHelperText sx={{ color: colors.RED }}>
                  {errors.terminals}
                </FormHelperText>
              )}
            </InputContainer> */}
            <InputContainer htmlFor="description">
              <div
                style={{
                  display: 'inline-flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                {t2('Description')}
                {formData?.description?.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: formData?.description?.length > 255 ? colors.RED : colors.GRAY,
                    }}
                  >
                    {`${formData?.description?.length}/255`}
                  </Typography>
                )}
              </div>
              <TextField
                name="description"
                placeholder={t2('Description')}
                multiline
                minRows={4}
                maxRows={4}
                value={formData?.description || ''}
                onChange={handleChange}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: colors.WHITE,
                  },
                }}
                InputProps={{ disableunderline: 'true' }}
                color="success"
                {...(errors?.description && {
                  error: true,
                  label: 'Error',
                  helperText: errors.description,
                })}
              />
            </InputContainer>
            <InputContainer htmlFor="trainingHours">
              {t2('training_hours')}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  name="trainingHours"
                  placeholder={'00'}
                  value={formData?.trainingHours}
                  onChange={event => {
                    setUnchanged(false);
                    const value = event.target.value;
                    setFormData({
                      ...formData,
                      trainingHours: value === '' ? '' : value,
                    });
                  }}
                  type="number"
                  inputProps={{ min: 0, max: 23 }}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: colors.WHITE,
                    },
                  }}
                  style={{ width: '4.1rem' }}
                  InputProps={{ disableunderline: 'true' }}
                  color="success"
                  {...(errors?.trainingHours && {
                    error: true,
                    label: 'Error',
                    helperText: errors.trainingHours,
                  })}
                />
                <span style={{ margin: '0 0.5rem 0 0.5rem', fontStyle: 'bold' }}>{` : `}</span>
                <Select
                  name="trainingHoursMinutes"
                  value={minutes || ''}
                  onChange={handleChange}
                  sx={{ backgroundColor: colors.WHITE, width: '4.25rem' }}
                  disableunderline="true"
                >
                  <MenuItem value="00">{`00`}</MenuItem>
                  <MenuItem value="15">{`15`}</MenuItem>
                  <MenuItem value="30">{`30`}</MenuItem>
                  <MenuItem value="45">{`45`}</MenuItem>
                </Select>
              </div>
            </InputContainer>
          </ColumnContainer>
          <ColumnContainer>
            <InputContainer htmlFor="priority">
              <div>
                {t2('CoursePriority')} {asterix}
              </div>
              {priorities?.length > 0 && (
                <SelectInput
                  name="priority"
                  placeholder={t2('Priority')}
                  value={formData?.priority || ''}
                  onChange={handleChange}
                  sx={{ backgroundColor: colors.WHITE }}
                  disableunderline="true"
                  options={priorities}
                  errortext={errors?.priority}
                />
              )}
            </InputContainer>
            <InputContainer htmlFor="expirationTime">
              <div>
                {t2('ExpirationTime')} {asterix}
              </div>
              {expirationTime.length > 0 && (
                <SelectInput
                  name="expirationTime"
                  placeholder={t2('ExpirationTime')}
                  value={formData?.expirationTime}
                  onChange={handleChange}
                  sx={{ backgroundColor: colors.WHITE }}
                  disableunderline="true"
                  options={expirationTime}
                />
              )}
              {errors?.expirationTime && (
                <FormHelperText sx={{ color: colors.RED }}>{errors.expirationTime}</FormHelperText>
              )}
            </InputContainer>
            <ImageUploadContainer>
              {t2('Thumbnail')}
              <ImageUploadComponent callback={handleImageUpload} />
            </ImageUploadContainer>
          </ColumnContainer>
        </div>
        <div style={buttonsContainer}>
          <Button
            variant="contained"
            style={unchanged ? buttonStyle(true) : buttonStyle(false)}
            type="submit"
            disabled={unchanged}
          >
            {t2('Save')}
          </Button>
        </div>
        <Modal
          open={openModal}
          setOpen={setOpenModal}
          Children={
            <SaveInfoModal
              accept={handleSubmit}
              cancel={handleClose}
              title={t2('BasicInfoModalTitle')}
              message={t2('BasicInfoModalMessage')}
            />
          }
        />
        <Alert message={t2('BasicInfoAlertSuccess')} open={open} setOpen={setOpen} />
      </form>
    </div>
  );
}
